import { PayloadAction } from "@reduxjs/toolkit";
import toolIndex from "toolConstants";
import { isObject } from "lodash";
import { calculateWallSpeakerTargetPoint } from "reduxModules/calculations/roomCalculations";
import { revalidateUcxEligibility } from "./room";

export const areaReducers = {
  /**
   * Sets project properties.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The project properties to update.
   * @param {string} [action.payload.name] - The project name.
   * @param {string} [action.payload.company] - The company name.
   * @param {string} [action.payload.designer] - The designer name.
   */
  setProject: (state: any, action: PayloadAction<{ name?: string; company?: string; designer?: string; locationId?: string }>) => {
    state.project = { ...state.project, ...action.payload };

    // If project name is being updated, also update the current area's name
    // This ensures Equipment List titles reflect the updated project name
    if (action.payload.name && state.activeTab && state.areas[state.activeTab]) {
      state.areas[state.activeTab].name = action.payload.name;
    }
  },

  /**
   * Sets the name of an area or the container.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The new name data.
   * @param {string} action.payload.name - The new name.
   * @param {string} [action.payload.id] - The ID of the area to rename (if applicable).
   */
  setName: (state: any, action: PayloadAction<{ id: string; name: string }>) => {
    (state.areas[state.activeTab] || state).name = action.payload.name;
  },

  /**
   * Adds a new area to the container (duplicate).
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} [action.payload] - Optional payload for area data.
   * @param {string} [action.payload] - The name of the area to duplicate.
   */
  duplicateArea: (state: any, action: PayloadAction<string>) => {
    const areaName = action.payload;
    const originalArea = state.areas[areaName];

    if (!originalArea) {
      return;
    }

    const lastRoomNumber = Object.keys(state.areas)[Object.keys(state.areas).length - 1].substring(5);
    const newId = '_room' + (Number(lastRoomNumber) + 1);
    const newName = `Copy of ${originalArea.name}`;
    const newArea = { ...originalArea };
    newArea.id = newId;
    newArea.name = newName;
    state.areas = { ...state.areas, [newId]: newArea };
    state.activeTab = newId;
    state.areaToUpdate = null;
  },

  /**
   * Updates an area based on the areaToUpdate state.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {boolean} action.payload - Whether to modify the area.
   */
  updateArea: (state: any, action: PayloadAction<boolean>) => {
    const modify = action.payload;
    const { area, update } = state.areaToUpdate; // , flag = false
    const { parameter, value } = update;
    if (modify) toolIndex[state.currentTool].resetArea(state, parameter, value);
    const newValue = modify ? value : state.areas[area][parameter];

    // Fix: Update specific parameter without replacing entire area object
    if (isObject(newValue)) {
      state.areas[area][parameter] = { ...state.areas[area][parameter], ...newValue };
    } else {
      state.areas[area][parameter] = newValue;
    }
    state.areaToUpdate = null;
  },

  resetArea: (state: any, action: PayloadAction<string | boolean>) => {
    if (action.payload) {
      toolIndex[state.currentTool].resetArea(state, action.payload);

      // After undoing speakers, reset wall speaker target point to default
      if (action.payload === 'speakers') {
        const area = state.areas[state.activeTab];
        const tool = state.currentTool;
        const speakerDef = toolIndex[tool]?.constants?.equipment?.speakers?.[area.speakers?.model];
        if (speakerDef?.isWallSpkr) {
          const constants = toolIndex[tool]?.constants;
          area.speakers.targetPoint = calculateWallSpeakerTargetPoint(area, constants);
        }
      }

      // Undo/reset changes mic (and speaker) counts, which is exactly the input
      // Tree Frog port-budget eligibility (`isDevioUcxEligible`) is computed from.
      // Without re-running this, a design that had fallen back to the Tesira +
      // UCC-UCX1000-T overflow route (`productFamily` flipped to 'Tesira' while
      // `platform` stayed 'Android') stays stuck on that stale fallback after the
      // mic count drops back under budget — the equipment list then mixes the
      // still-applicable Devio UCX bundle with the leftover Tesira/TCM-X à la
      // carte lines the overflow route added. Every other device-count-changing
      // reducer in room.ts (addDevice/removeDevice/setExpansionMicType/etc.)
      // already calls this; the device-specific undo path was the one gap.
      revalidateUcxEligibility(state);
    }
  },
};
